import { WithStyles } from '@material-ui/core/styles';
import React, { Component } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import * as dateLocales from 'date-fns/locale';
import clsx from 'clsx';
import { DateTime } from 'luxon';

import { FormControlError } from '#web-components/types/formControls';

import DateTimePickerInput from './components/DateTimePickerInput';
import HeaderWithYearPicker from './components/HeaderWithYearPicker';
import styles from './DateTimePicker.styles';
import TimePicker from './components/TimePicker';

import './styles.scss';

export interface DateTimePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  id: string;
  name?: string;
  error?: FormControlError;
  // eslint-disable-next-line react/no-unused-prop-types
  label?: string; // TODO: remove prop
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  enableDate?: boolean;
  enableTime?: boolean;
  inputDisabled?: boolean;
  tabIndex?: string;
  description?: string;
  checkDateDisabled?: (date?: string) => boolean;
  locale?: string;
}

export interface Props extends DateTimePickerProps, WithStyles<typeof styles> {}

export default class DateTimePicker extends Component<Props> {
  static defaultProps = {
    enableDate: true,
    enableTime: true,
  };

  private locale = this.props.locale || 'en';

  componentDidMount(): void {
    const { locale } = this;
    registerLocale(locale, dateLocales[locale as never]);
  }

  // eslint-disable-next-line class-methods-use-this
  getStringValue = (value: Date | null) => {
    return value ? DateTime.fromJSDate(value, { zone: 'utc' }).toISO() : null;
  };

  setValue = (newValue: Date | null) => {
    const { onChange, enableDate, enableTime } = this.props;
    if (!newValue) {
      onChange(null);
      return;
    }

    const dateTimeObj = DateTime.fromJSDate(newValue, { zone: 'utc' });
    let dateTime = dateTimeObj.toISO();
    if (enableDate && !enableTime) {
      dateTime = dateTimeObj.toLocal().toISODate();
    }
    if (!enableDate && enableTime) {
      dateTime = dateTimeObj.toISOTime();
    }
    onChange(dateTime);
  };

  filterDate = (value: Date) => {
    const { checkDateDisabled } = this.props;

    return checkDateDisabled
      ? checkDateDisabled(this.getStringValue(value) as string)
      : true;
  };

  render() {
    const {
      value,
      id,
      classes,
      inputDisabled,
      disabled,
      description,
      placeholder,
      name,
      tabIndex,
      format = 'yyyy-MM-dd HH:mm',
      enableDate,
      enableTime,
      error,
    } = this.props;
    const selected = (value && DateTime.fromISO(value).isValid) ? DateTime.fromISO(value).toJSDate() : null;
    return (
      <div className={clsx('datepickerStyles')}>
        <DatePicker
          id={id}
          name={name}
          disabled={disabled}
          placeholderText={placeholder}
          locale={this.locale}
          dateFormat={format}
          showTimeInput={enableTime}
          showTimeSelectOnly={!enableDate}
          selected={selected}
          onChange={this.setValue}
          tabIndex={tabIndex ? parseInt(tabIndex, 10) : undefined}
          filterDate={this.filterDate}
          calendarClassName={clsx('datepickerStyles', classes.calendar)}
          customInput={(
            <DateTimePickerInput
              id={id}
              error={error}
              name={name || id}
              inputDisabled={inputDisabled}
              description={description}
              format={format}
              locale={this.locale}
            />
          )}
          customTimeInput={<TimePicker />}
          portalId="datepicker-portal"
          showYearDropdown
          renderCustomHeader={(props) => (
            <HeaderWithYearPicker {...props} />
          )}
        />
      </div>
    );
  }
}
